<script setup lang="ts">
import { useFieldTexts, useValidationRules } from '@/composables';
import {
    useAttrs, ref, watch, nextTick, onMounted,
} from 'vue';
import type { VTextField } from 'vuetify/components';
import {
    formatForDateTimeInput,
    formatForDateInput,
    parseFromDateTimeInput,
    parseFromDateInput,
} from '@/utils/dateUtils';

type PropsType = {
    label?: string;
    placeholder?: string;
    variant?: VTextField['$props']['variant'];
    density?: VTextField['$props']['density'];
    color?: string;
    clearable?: boolean;
    loading?: boolean;
    isLabelTranslated?: boolean;
    showRequiredVisual?: boolean;
    min?: string;
    max?: string;
    inputType?: 'datetime-local' | 'date' | 'time';
    autoOpenPicker?: boolean;
    name?: string;
    dataType?: string;
};

const props = withDefaults(defineProps<PropsType>(), {
    variant: 'outlined',
    density: 'compact',
    clearable: false,
    loading: false,
    isLabelTranslated: true,
    showRequiredVisual: true,
    inputType: 'datetime-local',
    autoOpenPicker: false,
    dataType: 'datetime',
});

const emit = defineEmits<{
    'click:clear': [];
    'click:append': [];
    'click:prepend': [];
    'click:appendInner': [];
    'click:prependInner': [];
    focus: [isFocused: boolean];
    blur: [event: FocusEvent];
    input: [value: string | null];
    change: [value: string | null];
}>();

const model = defineModel<string | null>({
    default: null,
    get(value: string | null) {
        if (props.inputType === 'date') {
            return formatForDateInput(value);
        }

        return formatForDateTimeInput(value);
    },
    set(value: string | null) {
        if (props.inputType === 'date') {
            return parseFromDateInput(value);
        }

        return parseFromDateTimeInput(value);
    },
});

defineOptions({ inheritAttrs: false });

const attrs = useAttrs();
const inputRef = ref<VTextField>();

const { getLabel } = useFieldTexts({
    label: props.label,
    isTranslated: props.isLabelTranslated,
});

const { rules, isRequired } = useValidationRules(attrs);

const handleInput = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    const convertedValue = props.inputType === 'date' ? parseFromDateInput(value) : parseFromDateTimeInput(value);
    emit('input', convertedValue);
};

const handleChange = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    const convertedValue = props.inputType === 'date' ? parseFromDateInput(value) : parseFromDateTimeInput(value);
    emit('change', convertedValue);
};

onMounted(() => {
    const input = inputRef.value?.$el?.querySelector('input');
    if (input) {
        input.dataset.type = props.dataType;
    }
});

const openPicker = async () => {
    await nextTick();
    const inputElement = inputRef.value?.$el?.querySelector('input') as HTMLInputElement;
    if (inputElement) {
        inputElement.focus();

        if (inputElement.showPicker) {
            try {
                inputElement.showPicker();
            } catch {}
        }
    }
};

const handleFocus = (isFocused: boolean) => {
    emit('focus', isFocused);

    if (isFocused && props.autoOpenPicker) {
        const inputElement = inputRef.value?.$el?.querySelector('input') as HTMLInputElement;
        if (inputElement?.showPicker) {
            try {
                inputElement.showPicker();
            } catch {}
        }
    }
};

watch(
    () => props.autoOpenPicker,
    newValue => {
        if (newValue) {
            openPicker();
        }
    },
    { immediate: true },
);
</script>

<template>
    <VTextField
        ref="inputRef"
        v-model="model"
        :label="getLabel"
        :placeholder="placeholder"
        :variant="variant"
        :density="density"
        :color="color"
        :clearable="clearable"
        :loading="loading"
        :name="name"
        :class="[$attrs.class, { required: isRequired && showRequiredVisual }]"
        :min="min"
        :max="max"
        :type="inputType"
        v-bind="{
            ...$attrs,
            rules: rules,
        }"
        @click:clear="emit('click:clear')"
        @click:append="emit('click:append')"
        @click:prepend="emit('click:prepend')"
        @click:append-inner="emit('click:appendInner')"
        @click:prepend-inner="emit('click:prependInner')"
        @update:focused="handleFocus"
        @blur="emit('blur', $event)"
        @input="handleInput"
        @change="handleChange"
    >
        <template
            v-if="$slots.prepend"
            #prepend
        >
            <slot name="prepend" />
        </template>

        <template
            v-if="$slots.append"
            #append
        >
            <slot name="append" />
        </template>

        <template
            v-if="$slots.prependInner"
            #prepend-inner
        >
            <slot name="prepend-inner" />
        </template>

        <template
            v-if="$slots.appendInner"
            #append-inner
        >
            <slot name="append-inner" />
        </template>

        <template
            v-if="$slots.label"
            #label
        >
            <slot name="label" />
        </template>

        <template
            v-if="$slots.message"
            #message="{ message }"
        >
            <slot
                name="message"
                :message="message"
            />
        </template>

        <template
            v-if="$slots.details"
            #details
        >
            <slot name="details" />
        </template>
    </VTextField>
</template>
